import { ECreditApplicationStatus, EFinancingProgram, ELoanPurpose, EMerchantType } from '@if/core/types'
import { ActionList, DataGridPage, PageContainer, useCan, useIfUser, type Action } from '@if/mui'
import { access } from '@src/access'
import { AddApplicationButton, AddApplicationIconButton } from '@src/components/AddApplication'
import { useMerchant } from '@src/containers/App/MerchantProvider'
import { useUnreadCreditApplicationIds } from '@src/data/api/chat-message/chat-message-api'
import { useMerchantByParentId } from '@src/data/api/merchants-api/merchants-api'
import { useNewApplicationPrograms } from '@src/data/merchant-config-selector'
import { useCanInviteApplicant } from '@src/hooks/invitation-hooks'
import { HiddenMerchantCategories } from '@src/types/Constants'
import { CreditDashboardEntry } from '@src/types/CreditDashboardEntry'
import { UserPlus, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  applicationsFilterSchema,
  useApplicationsColumns,
  useApplicationsData,
  type ApplicationsFilters,
} from './browseCreditApplication-hooks'
import { ApplicationsFilterFields } from './components/CreditEntriesFiltersDialog'
import { useInvitationDialog } from './components/InvitationDialog'

/** Names spelled out in the merchant filter chip before it collapses to `+N`. */
const MAX_CHIP_MERCHANT_NAMES = 2

const BrowseCreditApplicationPage = () => {
  const { t } = useTranslation()
  const user = useIfUser()
  const hasDetailAccess = useCan(access.applicationDetail)
  const canCreateApplications = useCan(access.createApplication)
  const canManageUsers = useCan(access.manageUsers)
  const merchant = useMerchant()
  const canSendWelcomeMessage = useCanInviteApplicant()
  const unreadCreditApplicationIds = useUnreadCreditApplicationIds()
  const [bannerMerchants] = useMerchantByParentId(merchant.merchantTypeId === EMerchantType.Banner ? merchant.id : '')
  const navigate = useNavigate()

  // Shared Invite Applicant dialog (same one used by the sidebar action).
  const invite = useInvitationDialog()
  const handleInvite = () =>
    void invite.open({
      financingCompanyId: merchant.financingCompanyId,
      loanPurposeId: merchant.serviceCategory ?? null,
    })

  const serviceCategory = merchant.serviceCategory
  const isVeterinary = serviceCategory === ELoanPurpose.Veterinary
  const isHidden = serviceCategory ? HiddenMerchantCategories.includes(serviceCategory) : false
  const supportedPrograms = merchant.supportedFinancingPrograms ?? []
  const supportsMultiplePrograms = supportedPrograms.length > 1
  // Approximation of the old per-result-set check (all fetched apps are Products):
  // hide the approved-limit column when the merchant only offers Products.
  const onlyProductsApplications =
    supportedPrograms.length > 0 && supportedPrograms.every((program) => program === EFinancingProgram.Products)
  const newApplicationPrograms = useNewApplicationPrograms(merchant)

  const columns = useApplicationsColumns(isVeterinary, hasDetailAccess, unreadCreditApplicationIds)

  // Header actions collapse responsively (buttons → overflow menu) via ActionList.
  const headerActions: Action[] = []
  if (canManageUsers)
    headerActions.push({
      icon: Users,
      label: t('nav.manageUsers'),
      emphasis: 'normal',
      onClick: () => void navigate('/manage-users'),
    })
  if (canSendWelcomeMessage)
    headerActions.push({
      icon: UserPlus,
      label: t('nav.inviteApplicant'),
      emphasis: 'normal',
      onClick: handleInvite,
    })

  return (
    <PageContainer>
      <DataGridPage<CreditDashboardEntry, ApplicationsFilters>
        schema={applicationsFilterSchema}
        useData={useApplicationsData}
        columns={columns}
        searchField="search"
        searchPlaceholder={t('applicationHeader.searchPlaceholder')}
        dock
        tabsField="creditApplicationStatus"
        tabs={[
          { value: ECreditApplicationStatus.Active, label: t('common.applications') },
          { value: ECreditApplicationStatus.Draft, label: t('common.drafts') },
        ]}
        defaultLimit={10}
        canChangePageSize
        pageSizeOptions={[5, 10, 25]}
        cardBelow="md"
        storageKey="applications-filters"
        visibilityModel={{
          interestRate: !isHidden,
          merchantName: user?.isBannerUser ?? false,
          maxAmountFinanced: !onlyProductsApplications,
          financingProgramId: supportsMultiplePrograms,
        }}
        filterForm={{
          title: t('common.filters'),
          content: ({ form: { control, watch } }) => (
            <ApplicationsFilterFields control={control} watch={watch} merchant={merchant} />
          ),
          renderChip: {
            merchantIds: (value) => {
              const ids = Array.isArray(value) ? value : []
              if (ids.length === 0) return null
              const names = ids.map((id) => bannerMerchants.find((m) => m.id === id)?.name ?? id)
              const shown = names.slice(0, MAX_CHIP_MERCHANT_NAMES)
              const hidden = names.length - shown.length
              return `${t('common.merchant')}: ${shown.join(', ')}${hidden > 0 ? ` +${hidden}` : ''}`
            },
          },
        }}
        actions={
          headerActions.length > 0 || canCreateApplications ? (
            <>
              <ActionList actions={headerActions} variant="button" menuBelow="md" size="large" />
              {canCreateApplications && (
                <>
                  <AddApplicationButton
                    size="large"
                    sx={{ display: { xs: 'none', md: 'inline-flex' } }}
                    supportedFinancingPrograms={newApplicationPrograms}
                  />
                  <AddApplicationIconButton
                    size="large"
                    sx={{ display: { md: 'none' } }}
                    supportedFinancingPrograms={newApplicationPrograms}
                    color="primary"
                  />
                </>
              )}
            </>
          ) : null
        }
      />
    </PageContainer>
  )
}

export default BrowseCreditApplicationPage
